/**
 * Overview quick actions: create a school, manage B2C documents, and the
 * debug helpers (test school, auth debug, Sentry test).
 */
import { useRouter } from 'expo-router';
import * as Sentry from '@sentry/react-native';
import { apiRequest } from '../../../../services/apiClient';
import { confirmDialog, showDialog } from '../../../../utils/dialogs';
import { ROUTES } from '../../../../navigation/routes';

export function usePlatformQuickActions(loadPlatformData: () => Promise<void>) {
  const router = useRouter();

  const createSchool = () => {
    router.push(ROUTES.ADMIN_CREATE_SCHOOL);
  };

  const manageB2CDocuments = () => {
    router.push(ROUTES.TABS_DOCUMENTS);
  };

  const createTestSchool = async () => {
    const confirmed = await confirmDialog(
      'Create Test School',
      'This will create a test school "Test Academy" with a school admin account for testing B2B2C features. Continue?',
      'Create',
      'Cancel',
    );

    if (!confirmed) return;

    try {
      const result = await apiRequest<any>({ endpoint: '/schools/create-test-school', method: 'POST' });
      // A dialog, not a toast: the admin needs time to copy the credentials.
      showDialog(
        'Test School Created!',
        `School: ${result.school.name}\nAdmin Email: ${result.admin.email}\nPassword: ${result.admin.password}`,
      );

      loadPlatformData();
    } catch {
      showDialog('Error', 'Failed to create test school. Please try again.');
    }
  };

  const debugAuth = async () => {
    try {
      const result = await apiRequest<any>({ endpoint: '/schools/debug/auth', method: 'GET' });
      showDialog('Debug Auth Response:', JSON.stringify(result, null, 2));
    } catch (error) {
      console.error('Debug Auth Error:', error);
    }
  };

  const testSentry = async () => {
    const confirmed = await confirmDialog(
      'Test Sentry',
      'This will intentionally throw an error to test Sentry error reporting. Continue?',
      'Throw Error',
      'Cancel',
    );

    if (!confirmed) return;

    try {
      const testError = new Error('Sentry Test Error from SuperAdmin Dashboard');
      testError.name = 'SentryTestError';

      Sentry.captureException(testError, {
        tags: { triggeredFrom: 'PlatformAdminScreen' },
      });
      Sentry.captureMessage('Test message from SuperAdmin Dashboard', 'info');

      showDialog('Sentry Test Complete!', 'Test errors have been sent to Sentry.');
    } catch (error) {
      console.error('Sentry test failed:', error);
    }
  };

  return { createSchool, manageB2CDocuments, createTestSchool, debugAuth, testSentry };
}

export type PlatformQuickActions = ReturnType<typeof usePlatformQuickActions>;
